import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { Link } from 'gatsby';
import React, { useState } from 'react';

import { useAuth } from '../../contexts/auth';
import { LoginFormInputText } from '../login-form-input';
import { Submit } from '../submit';
import {
	action as actionStyle,
	centred,
	contactUs as contactUsStyle,
	innerContainer as innerContainerStyle,
	link as linkStyle,
	submitContainer as submitContainerStyle,
	submitErrorMessage as submitErrorMessageStyle,
} from './styles.module.css';

enum FormState {
	NotSubmitted,
	Submitted,
	SubmissionError,
}

enum LoginMode {
	Login = 1,
	ResetPassword,
	ResetPasswordSubmitted,
}
export interface FormData {
	username?: string;
	password?: string;
}

export interface LoginFormProps {
	onLogin(username: string, password: string): Promise<void>;
	onResetPassword(username: string): Promise<void>;
	onClose(): void;
	supportEmail?: string;
	formErrorMessage?: string;
}

export const LoginForm = ({
	onLogin,
	onResetPassword,
	onClose,
	supportEmail,
	formErrorMessage,
}: LoginFormProps): JSX.Element => {
	const [formState, setFormState] = useState<FormState>(FormState.NotSubmitted);
	const [loginMode, setLoginMode] = useState<LoginMode>(LoginMode.Login);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const auth = useAuth();

	const handleValidate = (values: FormData) => {
		const errors: FormData = {};
		if (!values.username) errors.username = 'Please fill in this field';
		else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.username))
			errors.username = 'Invalid email address';
		else delete errors.username;
		if (loginMode === LoginMode.Login && !values.password)
			errors.password = 'Please fill in this field';
		else delete errors.password;
		return errors;
	};

	const handleLogin = async ({ username, password }: FormData) => {
		if (!username || !password) return;
		try {
			await onLogin(username, password);
			setFormState(FormState.Submitted);
			onClose();
		} catch (error) {
			console.error(error);
			setErrorMessage(auth.error);
			setFormState(FormState.SubmissionError);
		}
	};

	const handlePasswordReset = async ({ username }: FormData) => {
		if (!username || typeof window === 'undefined') return;
		try {
			await onResetPassword(username);
			setFormState(FormState.Submitted);
			onClose();
			if (typeof window !== 'undefined')
				window.location.href = `/change-password/?username=${username}&mode=forgot_password`;
		} catch (error) {
			console.error(error);
			setErrorMessage((error as Error).message || JSON.stringify(error));
			setFormState(FormState.SubmissionError);
		}
	};

	const handleSubmit = (values: FormData) => {
		if (loginMode === LoginMode.Login) {
			handleLogin(values);
		} else {
			handlePasswordReset(values);
		}
	};

	const getSubmitPrompt = () => {
		switch (loginMode) {
			case LoginMode.ResetPassword:
				return 'Reset Password';
			default: {
				switch (formState) {
					case FormState.SubmissionError:
						return 'Retry';
					default:
						return 'Sign In';
				}
			}
		}
	};

	return (
		<div className={innerContainerStyle}>
			<h1>Login</h1>
			<Formik
				validate={handleValidate}
				validateOnChange
				validateOnBlur={false}
				initialValues={{ username: '', password: '' }}
				onSubmit={handleSubmit}
			>
				<Form>
					<div className={actionStyle}>
						<div>
							<LoginFormInputText name="username" type="email" placeholder="Username" />
							{loginMode === LoginMode.Login && (
								<LoginFormInputText name="password" type="password" placeholder="Password" />
							)}
							<div className={submitContainerStyle}>
								<Submit
									prompt={getSubmitPrompt()}
									supportEmail={supportEmail}
									errorMessage={formErrorMessage}
									submitError={formState === FormState.SubmissionError}
								/>
								{formState === FormState.SubmissionError && (
									<div className={submitErrorMessageStyle}>{errorMessage}</div>
								)}
							</div>
						</div>
					</div>
				</Form>
			</Formik>
			{loginMode === LoginMode.Login && (
				<span
					className={classNames(linkStyle, centred)}
					onClick={() => setLoginMode(LoginMode.ResetPassword)}
				>
					Forgot Password?
				</span>
			)}
			{/* @todo: Add 'Apply' link */}
			<div className={contactUsStyle}>
				Don't have a login?{' '}
				<Link to={'/contact-us'} className={linkStyle} onClick={onClose}>
					Contact Us
				</Link>
			</div>
		</div>
	);
};
